import { createClient } from '@/utils/supabase/client'
import type { Provider } from '@supabase/supabase-js'
import { clearDatabase } from '@/lib/dexie'
import { signOut, setUserRegionOnLocalStorage } from '@/lib/auth-service'
import { ENV } from '@/config/env'
import { clearAgencyIdCache } from './email-services'
import { Dispatch, SetStateAction } from 'react'

export interface OAuthOptions {
  provider: Provider
  redirectTo?: string
  queryParams?: Record<string, string>
  region?: string
}

export const signInWithOAuth = async (options: OAuthOptions) => {
  if (options.region) {
    setUserRegionOnLocalStorage(options.region)
  }

  const supabase = createClient()

  const redirectUrl =
    options.redirectTo || `${window.location.origin}/auth/callback`

  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: options.provider,
      options: {
        redirectTo: redirectUrl,
        queryParams: options.queryParams || {}
      }
    })

    if (error) {
      console.error(`❌ ${options.provider} OAuth error:`, error.message)
      throw error
    }

    // Redirect to OAuth provider URL
    if (data?.url) {
      window.location.href = data.url
      return { success: true, url: data.url }
    }

    return { success: false, error: 'No redirect URL received' }
  } catch (error) {
    console.error(`❌ ${options.provider} OAuth initiation failed:`, error)
    throw error
  }
}

export const signInWithGoogle = () => {
  // Use US region as default for Google sign-in
  const defaultRegion = 'us-east-1'

  return signInWithOAuth({
    provider: 'google',
    region: defaultRegion,
    queryParams: {
      access_type: 'offline',
      prompt: 'consent'
    }
  })
}

export async function performCompleteLogout({
  redirectTo,
  setIsLoggingOut
}: {
  redirectTo?: string
  setIsLoggingOut?: Dispatch<SetStateAction<boolean>>
}): Promise<void> {
  console.warn('[LOGOUT] Starting logout process')
  if (setIsLoggingOut) setIsLoggingOut(true)

  // Mark that logout is in progress to suppress UI toasts
  console.warn('[LOGOUT] Setting logout flag to TRUE')
  localStorage.setItem('is_logging_out', 'true')

  // Add a unique debug ID to track this logout session
  const logoutDebugId = Date.now().toString()
  localStorage.setItem('logout_debug_id', logoutDebugId)
  console.warn('[LOGOUT] Debug ID:', logoutDebugId)

  // Clear agency ID cache
  clearAgencyIdCache()

  try {
    console.warn('[LOGOUT] Calling signOut API')
    await signOut()
    console.warn('[LOGOUT] signOut API completed successfully')
  } catch (error) {
    console.error('[LOGOUT] Error during signOut:', error)
  }

  // Preserve logout indicator while clearing other storage
  const logoutId = localStorage.getItem('logout_debug_id')

  console.warn('[LOGOUT] Clearing localStorage and sessionStorage')
  localStorage.clear()
  sessionStorage.clear()

  // Set logout flag for the duration of logout process
  localStorage.setItem('is_logging_out', 'true')
  localStorage.setItem('logout_debug_id', logoutId || logoutDebugId)

  try {
    console.warn('[LOGOUT] Clearing Dexie database')
    await clearDatabase()
    console.warn('[LOGOUT] Dexie database cleared')
  } catch (error) {
    console.error('[LOGOUT] Failed to clear Dexie database:', error)
  }

  console.warn('[LOGOUT] Clearing cookies')
  const cookies = document.cookie.split(';')
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim()
    const cookieName = cookie.split('=')[0]

    if (cookieName !== 'waltz-ai-cookie-consent') {
      try {
        const { deleteCookie } = await import('@/lib/cookie-utils')
        deleteCookie(cookieName)
      } catch (error) {
        console.error('[LOGOUT] Failed to clear cookie:', cookieName, error)
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      }
    }
  }

  if (setIsLoggingOut) setIsLoggingOut(false)

  // Add delay to see toasts before redirect
  console.warn('User Sign-out: [LOGOUT] Waiting to see toast...')
  await new Promise(resolve => setTimeout(resolve, 3000));

  console.warn('User Sign-out: [LOGOUT] Redirecting to:', redirectTo || '/agency/signin')
  window.location.href = redirectTo || '/agency/signin'
}
